import { makeObservable } from 'mobx';

import { simpleHash } from '@feathr/hooks';
import type {
  Attributes,
  IBaseAttributes,
  ICollectionStore,
  IGetParams,
  TConstraints,
} from '@feathr/rachis';
import { isWretchError } from '@feathr/rachis';
import { Collection, Model, wretch } from '@feathr/rachis';

import type { TAdChannelKey } from './campaigns/types';
import type { IFlavors, IGoogleStats } from './stats';

export interface ICategoryConversionStats {
  category?: string;
  num?: number;
  roi?: number;
}

export interface IGoalConversionStats {
  goal_id?: string;
  num?: number;
  roi?: number;
  categories?: ICategoryConversionStats[];
}

export interface ISegmentConversionStats {
  goal_segment_id?: string;
  num?: number;
  roi?: number;
  goals?: IGoalConversionStats[];
}

export interface IConversionAttributionModelStats {
  num?: number;
  roi?: number;
  segments_breakdown?: ISegmentConversionStats[];
}

export interface IAttributions<T> {
  full?: T;
  linear?: T;
  positional?: T;
  temporal?: T;
  first_touch?: T;
  last_touch?: T;
}

export type TConversionStats = IAttributions<IConversionAttributionModelStats>;

export type TAttributionModel = keyof TConversionStats;

export function isAttributionModel(s: string): s is TAttributionModel {
  if (
    s === 'full' ||
    s === 'linear' ||
    s === 'positional' ||
    s === 'temporal' ||
    s === 'first_touch' ||
    s === 'last_touch'
  ) {
    return true;
  }
  return false;
}

export interface IHostCounts {
  [key: string]: number;
}

export interface IHosts {
  ad_view?: IHostCounts;
  ad_click?: IHostCounts;
  page_view?: IHostCounts;
  page_link_click?: IHostCounts;
  email_view?: IHostCounts;
  email_link_click?: IHostCounts;
}

export interface ILocation {
  lat: number;
  lng: number;
  count: number;
}

export type TTrafficConversionAttribution = Partial<IAttributions<number>>;

export interface ITrafficSource {
  views: number;
  sessions: number;
  conversions: TTrafficConversionAttribution;
}

export interface ITrafficSources {
  email: ITrafficSource;
  search: ITrafficSource;
  facebook: ITrafficSource;
  twitter: ITrafficSource;
  linkedin: ITrafficSource;
  youtube: ITrafficSource;
  tumblr: ITrafficSource;
  pinterest: ITrafficSource;
  reddit: ITrafficSource;
  feathr: ITrafficSource;
  other: ITrafficSource;
  [key: string]: ITrafficSource;
}

export interface IKeywordStats {
  num_clicks: number;
  num_views: number;
  spend: number;
  term: string;
}

export interface ITopGoal {
  goal_id: string;
  count: number;
}

export interface IUrlClicks {
  url: string;
  clicks: number;
}

export type TChannelStats<T> = {
  [K in TAdChannelKey]?: Partial<Omit<T, 'channels'>>;
};

// Omni/Pixl Plus specific stats
export interface IOmniStats {
  num_devices_reached?: {
    avg_per_person: number;
    new: number;
    total_targetable: number;
  };
  num_persons_reached?:
    | {
        device?: number;
        email?: number;
        lookalike?: number;
        total?: number;
      }
    // New grouped structure
    | {
        main?: {
          device: number;
          email: number;
          total: number;
        };
        lookalike?: {
          device: number;
          email: number;
          total: number;
        };
        meta?: {
          total: number;
        };
        meta_lookalike?: {
          total: number;
        };
      };
}

export interface IAggregatedStats extends IOmniStats {
  clicks_by_url?: {
    [hash: string]: IUrlClicks;
  };
  conversions?: TConversionStats;
  flavors?: IFlavors;
  hosts?: IHosts;
  locations?: ILocation[];
  num_clicks?: number;
  num_clicks_active?: number;
  num_clicks_ad?: number;
  num_clicks_email?: number;
  num_clicks_page?: number;
  num_conversions?: number;
  num_crumbs?: number;
  num_deliveries_email?: number;
  num_impressions_bid?: number;
  num_impressions_won?: number;
  num_new_users_active?: number;
  num_new_users_active_reachable?: number;
  num_users_active?: number;
  num_not_sent?: number;
  num_persons_unsubscribed?: number;
  num_sends_email?: number;
  num_submissions?: number;
  num_targeted?: number;
  num_views?: number;
  num_views_ad?: number;
  num_views_email?: number;
  num_views_page?: number;
  platforms?: {
    [s: string]: number;
  };
  spend?: number;
  traffic?: ITrafficSources;
  unfiltered?: Partial<IAggregatedStats>;

  channels?: TChannelStats<IAggregatedStats>;

  // Google campaigns
  terms?: IKeywordStats[];
  conversions_roi?: number;

  // Email campaigns
  clicks_per_person?: number;
  email_open_rate?: number;
  hard_bounce_rate?: number;
  opens_per_person?: number;
  successful_delivery_rate?: number;
  unique_deliveries?: number;

  // Shared by email campaigns and accounts
  email_click_through_rate?: number;

  // Account stats
  ad_click_through_rate?: number;
  google?: IGoogleStats;
  num_ad_clicks?: number;
  num_ad_views?: number;
  num_email_bounces_and_complaints?: number;
  num_email_clicks?: number;
  num_email_opens?: number;
  num_email_sends?: number;
  num_persons_converted?: number;
  num_persons_created?: number;
  num_persons_created_by_data_integration?: number;
  num_persons_created_by_import_and_integration?: number;
  num_persons_created_by_ad_campaign?: number;
  num_persons_created_by_import?: number;
  num_persons_created_by_pixel?: number;
  // num_persons_reached?: number;
  num_persons_with_ad_clicks?: number;
  num_persons_with_ad_views?: number;
  num_persons_with_page_link_clicks?: number;
  num_persons_with_page_views?: number;
  num_persons_with_email_clicks?: number;
  num_persons_with_email_opens?: number;

  // Goals
  /** The top three goals within a given date range. */
  top_goals?: ITopGoal[];
}

/**
 * Aggregated stats shape returned by `GET /stats/aggregated?model=form&...`.
 * Extends the base shape with donation-form-specific fields computed by the
 * backend's `FormStatsAggregationController`. All fields are optional because
 * the backend may omit zeros.
 */
export interface IFormAggregatedStats extends IAggregatedStats {
  average_donation?: number;
  credits_rate?: number;
  date_last_donation?: string;
  donations_count?: number;
  donations_credits?: number;
  donations_credits_count?: number;
  donations_recurring_count?: number;
  donations_recurring_total?: number;
  donations_total?: number;
  growth_credits?: number;
  submission_rate?: number;
  unique_views?: number;
}

export interface IAggregatedStatsDocument extends IAggregatedStats, IBaseAttributes {}

export class AggregatedStat extends Model<IAggregatedStatsDocument> {
  public readonly className = 'DailyStat';

  public get constraints(): TConstraints<IAggregatedStats> {
    return {};
  }
}

export type TAggregatedStatsModel =
  | 'account'
  | 'campaign'
  | 'creative'
  | 'event'
  | 'flight'
  | 'segment'
  | 'form';

interface IStatsGetParams extends IGetParams<IAggregatedStats> {
  model: TAggregatedStatsModel;
}

export class AggregatedStats extends Collection<AggregatedStat> {
  constructor(
    initialModels: Array<Partial<Attributes<AggregatedStat>>>,
    store: ICollectionStore = {},
  ) {
    super(initialModels, store);

    makeObservable(this);
  }

  public getModel(attributes: Partial<IAggregatedStats>): AggregatedStat {
    return new AggregatedStat(attributes);
  }

  public getClassName(): string {
    return 'stats_aggregated';
  }

  public override url(): string {
    return `${this.getHostname()}stats/aggregated`;
  }

  public aggregate(
    params: IStatsGetParams,
    callback?: (model: AggregatedStat) => void,
  ): AggregatedStat {
    // Hash params; be sure to exclude the URL and question mark
    const id = simpleHash(this.paramsToUrl(params).replace(this.url(), '').replace(/^\?/, ''));
    return super.get(id, {}, params, {}, callback);
  }

  public override async fetchModel(
    model: AggregatedStat,
    params: IGetParams<IAggregatedStats> = {},
    fetchOptions: Partial<RequestInit> = {},
    callback?: (model: AggregatedStat) => void,
  ): Promise<AggregatedStat> {
    const url = this.paramsToUrl(params);
    model.isPending = true;
    const response = await wretch<IAggregatedStatsDocument>(url, {
      method: 'GET',
      headers: this.getHeaders(),
      ...fetchOptions,
    });
    if (isWretchError(response)) {
      this.processErrorResponse(model, response.error);
      return model;
    }
    const out = this.processJSONResponse({ ...response.data }, params.only);
    if (callback) {
      callback(out);
    }
    return out;
  }
}
